"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { CheckIcon, ChevronDownIcon, CloseIcon, SearchBlack } from "@/components/ui/primitives/icons";
import { cn } from "@/lib/utils";

/* ── Types ── */

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onChange: (value: string) => void;
  /** @default "선택하세요" */
  placeholder?: string;
  /** @default "100%" */
  w?: string;
  /** @default 5 */
  maxVisibleItems?: number;
  hasError?: boolean;
  className?: string;
}

/* ── Component ── */

const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = "선택하세요",
      w = "100%",
      maxVisibleItems = 5,
      hasError = false,
      className,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const selectedLabel = useMemo(
      () => options.find((o) => o.value === value)?.label,
      [options, value]
    );

    const filtered = useMemo(() => {
      if (!query) return options;
      const q = query.toLowerCase();
      return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    // Close on outside click (account for portal dropdown)
    const portalRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;
        const inContainer = containerRef.current?.contains(target);
        const inPortal = portalRef.current?.contains(target);
        if (!inContainer && !inPortal) {
          setOpen(false);
          setQuery("");
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Position dropdown relative to trigger via portal, follow on scroll.
    // 뷰포트 하단 공간이 부족하면 위쪽으로 flip 해서 열림이 가려지지 않도록 한다.
    const updatePosition = useCallback(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const measuredHeight = portalRef.current?.offsetHeight ?? 0;
      const estimatedHeight = maxVisibleItems * 40 + 60; // 항목 40px × N + 검색박스 ~60px
      const dropdownHeight = measuredHeight > 0 ? measuredHeight : estimatedHeight;
      const margin = 8;
      const overflowBelow = rect.bottom + dropdownHeight + margin > window.innerHeight;
      const fitsAbove = rect.top - dropdownHeight - margin >= 0;
      const placeAbove = overflowBelow && fitsAbove;

      setDropdownStyle({
        position: "fixed",
        top: placeAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }, [maxVisibleItems]);

    useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
    }, [open, updatePosition]);

    useEffect(() => {
      if (!open) return;
      const handleScroll = (e: Event) => {
        if (portalRef.current?.contains(e.target as Node)) return;
        updatePosition();
      };
      window.addEventListener("scroll", handleScroll, true);
      return () => window.removeEventListener("scroll", handleScroll, true);
    }, [open, updatePosition]);

    // Focus search input when opened
    useEffect(() => {
      if (open) {
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }, [open]);

    // Scroll highlighted item into view
    useEffect(() => {
      if (highlightIdx < 0 || !listRef.current) return;
      const el = listRef.current.children[highlightIdx] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }, [highlightIdx]);

    const handleSelect = useCallback(
      (opt: ComboboxOption) => {
        onChange(opt.value);
        setOpen(false);
        setQuery("");
        setHighlightIdx(-1);
      },
      [onChange]
    );

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange("");
        setQuery("");
      },
      [onChange]
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIdx((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIdx((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (highlightIdx >= 0 && filtered[highlightIdx]) {
            handleSelect(filtered[highlightIdx]);
          }
          break;
        case "Escape":
          setOpen(false);
          setQuery("");
          break;
      }
    };

    return (
      <div ref={containerRef} className="relative" style={{ width: w }}>
        {/* Trigger button */}
        <button
          ref={(node) => {
            triggerRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
          }}
          type="button"
          className={cn(
            "flex items-center w-full",
            "h-11 px-4 rounded-4",
            "border-2 bg-white",
            "cursor-pointer",
            "transition-[border-color] duration-200",
            hasError ? "border-danger-400" : "border-gray-300",
            open && !hasError && "border-primary-700",
            className
          )}
          onClick={() => {
            setOpen((v) => !v);
            setQuery("");
            setHighlightIdx(-1);
          }}
        >
          <span
            className={cn(
              "flex-1 text-left truncate text-[15px]",
              selectedLabel ? "font-semibold text-gray-900" : "font-normal text-gray-700"
            )}
          >
            {selectedLabel ?? placeholder}
          </span>

          {/* Clear button replaces chevron when value is selected */}
          {value ? (
            <span
              role="button"
              tabIndex={-1}
              className="shrink-0 flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
              onClick={handleClear}
              onKeyDown={(e) => { if (e.key === "Enter") handleClear(e as unknown as React.MouseEvent); }}
            >
              <CloseIcon size={16} />
            </span>
          ) : (
            <ChevronDownIcon
              size={20}
              className={cn(
                "shrink-0 text-gray-700 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          )}
        </button>

        {/* Dropdown popover via portal */}
        {open && createPortal(
          <div
            ref={portalRef}
            className={cn(
              "z-[9999] rounded-4",
              "bg-white",
              "border border-gray-300",
              "shadow-[0px_0px_10px_5px_rgba(136,136,136,0.15)]",
              "overflow-hidden"
            )}
            style={dropdownStyle}
          >
            {/* Search input */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-primary-200 bg-primary-100/30">
              <SearchBlack size={16} className="shrink-0 text-gray-500" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full bg-transparent border-none outline-none ring-0 focus:border-none focus:outline-none focus:ring-0 shadow-none focus:shadow-none text-[14px] text-gray-900 placeholder:text-gray-500 caret-primary-700"
                placeholder="검색..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlightIdx(-1);
                }}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              className="p-1"
              style={{
                maxHeight: `${maxVisibleItems * 40}px`,
                overflowY: "auto",
              }}
            >
              {filtered.length === 0 && (
                <div className="px-3 py-2.5 text-[14px] text-gray-600">
                  검색 결과가 없습니다
                </div>
              )}
              {filtered.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isHighlighted = idx === highlightIdx;
                return (
                  <div
                    key={opt.value}
                    className={cn(
                      "flex items-center gap-2",
                      "w-full h-10 px-3 py-3 rounded-4",
                      "text-[15px] font-semibold leading-[140%] text-gray-900",
                      "cursor-pointer",
                      isSelected && "bg-primary-200 text-primary-900",
                      isHighlighted && !isSelected && "bg-gray-200",
                      !isSelected && !isHighlighted && "hover:bg-gray-200"
                    )}
                    onClick={() => handleSelect(opt)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                  >
                    {isSelected && (
                      <CheckIcon size={16} className="shrink-0 text-primary-700" />
                    )}
                    <span className={isSelected ? "ml-0" : "ml-6"}>{opt.label}</span>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }
);
Combobox.displayName = "Combobox";

export { Combobox };
